/**
 * useCourseCustomCode
 *
 * Client-only composable that watches the active course product's custom code
 * (customJs, customCss, customHeader, customFooter) and injects/removes
 * styles, scripts, and tracking code into the live DOM for host-rendered
 * course views (Compass Mobile, Aura / GokollabTheme, and Live Sessions).
 *
 * On Desktop MFA themes (Classic, NewYork, Neue, NeoClassic), the remote
 * membership-hmi-preview MFA executes its own courseCustomCodeSession, so this
 * composable skips host injection to prevent duplicate script execution.
 */

import { computed, onMounted, onUnmounted, watch } from 'vue'

import { useRoute } from 'nuxt/app'

import type { IProductDetail } from '@courses/types-layer/types/product'

import { isMobileOrTablet } from '../../../common/core-layer/utils/core.utils'
import { useLocationStore } from '../../../common/location-layer/store/location.store'
import { useSentry } from '../../../common/error-monitoring-layer/composables/useSentry'
import { applySupportChatWidgetMobilePositioning } from '../../../clientportal/support-chat-layer/utils/supportChatWidgetDom'

import { useProductQueryStore } from '../stores/product.query-store'
import { useProductTenantContextStore } from '../stores/product-tenant-context.store'
import type { PortalCustomCode } from '../../../common/location-layer/types/location'
import type { CourseCustomCode } from '../types/course-custom-code.types'

export const COURSE_CUSTOM_CODE_ATTR = 'data-course-custom-code'

/** Stable head element IDs for injected CSS/JS (tests and deterministic DOM queries). */
export const COURSE_CUSTOM_CODE_INJECTED_STYLE_ID = 'course-custom-code-injected-css'
export const COURSE_CUSTOM_CODE_INJECTED_SCRIPT_ID = 'course-custom-code-injected-js'

export const COURSE_CUSTOM_CODE_HEADER_ANCHOR_ID = 'courses-siteCustomHeader'
export const COURSE_CUSTOM_CODE_FOOTER_ANCHOR_ID = 'courses-siteCustomFooter'

const STANDALONE_COURSE_PRODUCT_PATH_RE = /^\/courses\/products\/([^/]+)/
const COMMUNITY_COURSE_PRODUCT_PATH_RE = /^\/communities\/groups\/[^/]+\/learning\/courses\/(?:products\/)?([^/]+)/

/**
 * Extracts productId from route params or path.
 */
export function extractCourseProductId(route: { path?: string; params?: Record<string, unknown> }): string | null {
  const pid = route.params?.productId
  if (typeof pid === 'string' && pid.trim().length > 0) {
    return pid.trim()
  }
  if (typeof route.path === 'string') {
    const standaloneMatch = route.path.match(STANDALONE_COURSE_PRODUCT_PATH_RE)
    if (standaloneMatch?.[1]) return standaloneMatch[1]

    const communityMatch = route.path.match(COMMUNITY_COURSE_PRODUCT_PATH_RE)
    if (communityMatch?.[1]) return communityMatch[1]
  }
  return null
}

/**
 * Checks if the route represents an active course product surface.
 */
export function isActiveCourseProductRoute(route: { path?: string; params?: Record<string, unknown> }): boolean {
  return Boolean(extractCourseProductId(route))
}

/**
 * Returns true when the host Nuxt app renders the course view directly
 * (mobile/tablet viewports, Aura / GokollabTheme, or live-sessions page).
 * Returns false on Desktop MFA themes where RemoteMembershipPreviewApp runs
 * its own courseCustomCodeSession.
 */
export function shouldHostApplyCourseCustomCode(product: IProductDetail | null, path?: string): boolean {
  if (isMobileOrTablet()) return true
  if (typeof path === 'string' && path.includes('/live-sessions')) return true
  const templateId = String(product?.customizations?.templateId ?? '')
    .trim()
    .toLowerCase()
  return templateId === 'gokollabtheme' || templateId === 'aura' || templateId === 'gokollab'
}

/** Serialise course custom code to a stable string for content-equality checks. */
export function serializeCourseCustomCode(productId: string, code: CourseCustomCode): string {
  return JSON.stringify({
    productId,
    customJs: code.customJs ?? '',
    customCss: code.customCss ?? '',
    headerCode: code.customHeader ?? '',
    footerCode: code.customFooter ?? '',
  })
}

function normalizeComparableCode(code: string | null | undefined, decodeEntities = false): string {
  if (!code) return ''
  const normalized = decodeEntities ? decodeHtmlEntities(code) : code
  return normalized.trim()
}

/**
 * Strips course custom code fields that are identical to already-active Client Portal
 * custom code (`locationStore.customCode` with `liveMode !== false`) so the same
 * footer, header, JS, or CSS is never executed twice.
 */
export function deduplicateCourseCustomCode(courseCode: CourseCustomCode, portalCode?: PortalCustomCode | null): CourseCustomCode {
  if (!portalCode || portalCode.liveMode === false) {
    return courseCode
  }

  const courseCss = normalizeComparableCode(courseCode.customCss)
  const portalCss = normalizeComparableCode(portalCode.customCss)

  const courseJs = normalizeComparableCode(courseCode.customJs)
  const portalJs = normalizeComparableCode(portalCode.customJs)

  const courseHeader = normalizeComparableCode(courseCode.customHeader, true)
  const portalHeader = normalizeComparableCode(portalCode.headerCode, true)

  const courseFooter = normalizeComparableCode(courseCode.customFooter, true)
  const portalFooter = normalizeComparableCode(portalCode.footerCode, true)

  return {
    customCss: courseCss && courseCss === portalCss ? null : courseCode.customCss,
    customJs: courseJs && courseJs === portalJs ? null : courseCode.customJs,
    customHeader: courseHeader && courseHeader === portalHeader ? null : courseCode.customHeader,
    customFooter: courseFooter && courseFooter === portalFooter ? null : courseCode.customFooter,
  }
}

/**
 * Wrap raw JS in a try/catch so errors in customer code don't propagate to
 * our own error monitoring. Mirrors sanitizeJavaScriptCode.
 */
export function sanitizeCourseJs(jsCode: string): string {
  const TAG_PATTERN = /<\/?[a-zA-Z][^<>]*>/g
  let stripped = jsCode
  let previous: string
  do {
    previous = stripped
    stripped = previous.replace(TAG_PATTERN, '')
  } while (stripped !== previous)

  return `try {
  ${stripped.trim()}
} catch (error) {
  console.error('Course custom JS error:', error);
}`
}

/** Remove all previously injected course custom-code elements. */
export function removeInjectedCourseCustomCodeElements(): void {
  if (typeof document === 'undefined') return

  document.querySelectorAll(`[${COURSE_CUSTOM_CODE_ATTR}]`).forEach(el => el.remove())

  const anchorIds = [COURSE_CUSTOM_CODE_HEADER_ANCHOR_ID, COURSE_CUSTOM_CODE_FOOTER_ANCHOR_ID]
  for (const id of anchorIds) {
    const el = document.getElementById(id)
    if (el) {
      el.innerHTML = ''
    }
  }
}

function injectCourseCss(css: string): void {
  if (!css || typeof document === 'undefined') return
  const style = document.createElement('style')
  style.id = COURSE_CUSTOM_CODE_INJECTED_STYLE_ID
  style.setAttribute('type', 'text/css')
  style.setAttribute(COURSE_CUSTOM_CODE_ATTR, 'css')
  style.innerHTML = css
  document.head.appendChild(style)
}

function injectCourseJs(js: string): void {
  if (!js || typeof document === 'undefined') return
  const sanitized = sanitizeCourseJs(js)

  try {
    new Function(sanitized)
  } catch (e) {
    console.error('Course custom JS is invalid — skipping injection:', e)
    return
  }

  const script = document.createElement('script')
  script.id = COURSE_CUSTOM_CODE_INJECTED_SCRIPT_ID
  script.setAttribute('type', 'text/javascript')
  script.setAttribute(COURSE_CUSTOM_CODE_ATTR, 'js')
  script.innerHTML = sanitized
  document.head.appendChild(script)
}

export function decodeHtmlEntities(code: string): string {
  if (!code) return ''
  return code
    .replace(/\\x3C/g, '<')
    .replace(/\\x3E/g, '>')
    .replace(/\\x22/g, '"')
    .replace(/\\x27/g, "'")
    .replace(/\\x2F/g, '/')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#0*39;/gi, "'")
    .replace(/&amp;/gi, '&')
    .replace(/<!--[\s\S]*?-->/g, '')
}

function injectCourseHtml(targetId: string, html: string, attrValue: string): void {
  if (!html || typeof document === 'undefined') return
  const target = document.getElementById(targetId)
  if (!target) return

  const decoded = decodeHtmlEntities(html)
  const normalized = decoded.includes('<') ? decoded : `<script>${decoded}<\/script>`

  const parser = new DOMParser()
  const doc = parser.parseFromString(normalized, 'text/html')

  const allNodes = [...Array.from(doc.head.childNodes), ...Array.from(doc.body.childNodes)]

  allNodes.forEach(node => {
    if (node.nodeName === 'SCRIPT') {
      const original = node as HTMLScriptElement
      const script = document.createElement('script')
      script.setAttribute(COURSE_CUSTOM_CODE_ATTR, attrValue)
      if (original.src) {
        script.src = original.src
        script.async = original.hasAttribute('async')
      } else {
        script.innerHTML = original.innerHTML
      }
      Array.from(original.attributes).forEach(attr => {
        if (attr.name !== 'src') script.setAttribute(attr.name, attr.value)
      })
      target.appendChild(script)
    } else {
      const clone = (node as Element).cloneNode?.(true) as Element
      if (clone && clone.setAttribute) clone.setAttribute(COURSE_CUSTOM_CODE_ATTR, attrValue)
      if (clone) target.appendChild(clone)
    }
  })
}

export function applyCourseCustomCode(code: CourseCustomCode): void {
  removeInjectedCourseCustomCodeElements()

  injectCourseCss(code.customCss ?? '')
  injectCourseJs(code.customJs ?? '')
  injectCourseHtml(COURSE_CUSTOM_CODE_HEADER_ANCHOR_ID, code.customHeader ?? '', 'header')
  injectCourseHtml(COURSE_CUSTOM_CODE_FOOTER_ANCHOR_ID, code.customFooter ?? '', 'footer')
}

/**
 * Call this composable once in CPRootContainer.
 * Watches the active course product and applies custom code on host-rendered course routes.
 */
export function useCourseCustomCode(): void {
  if (process.server) return

  const route = useRoute()
  const locationStore = useLocationStore()
  const productTenantContextStore = useProductTenantContextStore()
  const effectiveLocationId = computed(() => productTenantContextStore.productLocationId || locationStore.locationId)
  const queryStore = useProductQueryStore(effectiveLocationId)
  const { setTag } = useSentry()

  let isMounted = false
  let lastAppliedProductId: string | null = null
  let lastAppliedContent: string | null = null

  const activeProductId = computed(() => extractCourseProductId(route) ?? '')

  // Query declared in setup scope with reactive getter so TanStack Vue Query stays reactive
  const productDetailQuery = queryStore.fetchProductQuery(() => activeProductId.value)

  const productDetail = computed<IProductDetail | null>(() => {
    return productDetailQuery.data.value ?? null
  })

  function clearAppliedCourseCustomCode(): void {
    const hadAppliedState = lastAppliedContent !== null || lastAppliedProductId !== null
    lastAppliedProductId = null
    lastAppliedContent = null
    if (hadAppliedState) {
      removeInjectedCourseCustomCodeElements()
      setTag('customCode', 'false')
    }
  }

  function applyIfChanged(pid: string, product: IProductDetail | null, path?: string): void {
    if (!isMounted) return

    if (!pid) {
      clearAppliedCourseCustomCode()
      return
    }

    if (!product) {
      // Preserve existing injected custom code across transient query loading states
      // (e.g. navigating from product route to lesson route of the same product).
      if (pid === lastAppliedProductId) {
        return
      }
      clearAppliedCourseCustomCode()
      return
    }

    if (!shouldHostApplyCourseCustomCode(product, path)) {
      clearAppliedCourseCustomCode()
      return
    }

    const rawCode: CourseCustomCode = {
      customJs: product.customJs,
      customCss: product.customCss,
      customHeader: product.customHeader,
      customFooter: product.customFooter,
    }
    const code = deduplicateCourseCustomCode(rawCode, locationStore.customCode)

    const content = serializeCourseCustomCode(pid, code)
    if (pid === lastAppliedProductId && content === lastAppliedContent) return
    lastAppliedProductId = pid
    lastAppliedContent = content

    const hasCustomCode = Boolean(code.customJs || code.customCss || code.customHeader || code.customFooter)
    setTag('customCode', hasCustomCode ? 'true' : 'false')

    if (hasCustomCode) {
      applyCourseCustomCode(code)
      if (isMobileOrTablet()) {
        applySupportChatWidgetMobilePositioning()
      }
    } else {
      removeInjectedCourseCustomCodeElements()
    }
  }

  onMounted(() => {
    isMounted = true
    applyIfChanged(activeProductId.value, productDetail.value, route.path)
  })

  watch([() => activeProductId.value, () => productDetail.value, () => route.path, () => locationStore.customCode], ([pid, prod, path]) => {
    applyIfChanged(pid, prod, path)
  })

  onUnmounted(() => {
    isMounted = false
    lastAppliedProductId = null
    lastAppliedContent = null
    removeInjectedCourseCustomCodeElements()
  })
}
